import React, { useEffect, useState } from "react"
import * as Yup from "yup"
import { Form, Formik } from "formik"
import { toast } from "react-toastify"
import { AxiosError } from "axios"
import { CandidatureModel, IHttpErrorResponseModel } from "@common-models/*"
import { CONST_CANDIDAT_TYPE_POSTULANT, CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR } from "@common-constants/*"
import MyAlert from "@common-utils/MyAlert"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"

// Sub-components
import { InterviewCalendar } from "./components/InterviewCalendar"
import { InterviewParticipants } from "./components/InterviewParticipants"
import { ICandidatProgrammerReunionFormModel } from "../core/_models"
import { useAuth } from "../../../../../AppContext"
import { getCandidatures, programmerEntretienMeet } from "../../core/_requests"
import { InterviewConfiguration } from "./components/InterviewConfiguration"
import { InterviewContext } from "./components/InterviewContext"
import clsx from "clsx"

const STORAGE_KEY_HIDE_INTRO = "hide_programmer_reunion_intro"

const ProgrammerReunion = ({ candidat, handleSubmitCallBack }: ICandidatProgrammerReunionFormModel) => {
     const { currentUser } = useAuth()
     const [candidatures, setCandidatures] = useState<CandidatureModel[]>([])
     const [isLoadingCandidatures, setIsLoadingCandidatures] = useState(false)

     // Logique d'affichage de l'introduction
     const [showIntro, setShowIntro] = useState<boolean>(() => {
          return localStorage.getItem(STORAGE_KEY_HIDE_INTRO) !== "true"
     })

     useEffect(() => {
          if (candidat.type === CONST_CANDIDAT_TYPE_POSTULANT && candidat.id) {
               setIsLoadingCandidatures(true)
               getCandidatures(candidat.id)
                    .then(r => setCandidatures(r.data))
                    .catch(() => toast.error("Erreur de récupération des offres"))
                    .finally(() => setIsLoadingCandidatures(false))
          }
     }, [candidat.id, candidat.type])

     const handleContinue = (dontShowAgain: boolean) => {
          if (dontShowAgain) {
               localStorage.setItem(STORAGE_KEY_HIDE_INTRO, "true")
          }
          setShowIntro(false)
     }

     const schema = Yup.object().shape({
          type: Yup.string().required(),
          lieu: Yup.string().required(),
          adresse: Yup.string().when("lieu", { is: "LIEU_AGENCE", then: s => s.required("Obligatoire"), otherwise: s => s.nullable() }),
          reunionDate: Yup.string().required(),
          reunionTitre: Yup.string().required(),
          reunionDureeEnMinutes: Yup.number().required(),
          autresParticipants: Yup.array().required().min(1),
          organisateurs: Yup.array().required().min(1),
          commentaire: Yup.string().when("isCommentaire", { is: true, then: s => s.required("Obligatoire") }),
     })

     const initialValues = {
          type: "TYPE_ENTRETIEN_CANDIDAT",
          lieu: "LIEU_MEET",
          adresse: "",
          reunionTitre: `Entretien ${candidat.prenomNom} ( entretien candidat )`,
          reunionDate: "",
          reunionDureeEnMinutes: 60,
          organisateurs: [currentUser?.mailProfessionnel!],
          autresParticipants: [candidat?.email!],
          annonce: candidatures.length === 1 ? candidatures[0].annonce?.id : undefined,
          isCommentaire: false,
          commentaire: "",
     }

     const getTitreReunion = (type: string) => {
          if (type === "TYPE_ENTRETIEN_CANDIDAT") return `Entretien ${candidat.prenomNom} ( entretien candidat )`
          const initials = (candidat.prenom?.charAt(0) || "") + (candidat.nom?.substring(0, 2) || "")
          return `Entretien de qualification - ${initials.toUpperCase()}`
     }

     const handleSubmit = (values: any, helpers: any) => {
          programmerEntretienMeet(
               candidat.id,
               values.type,
               values.lieu,
               values.adresse,
               values.reunionTitre,
               values.reunionDate,
               values.reunionDureeEnMinutes,
               values.organisateurs,
               values.isCommentaire,
               values.autresParticipants,
               values.annonce,
               values.commentaire
          )
               .then(() => {
                    handleSubmitCallBack()
                    toast.success("Entretien programmé.")
               })
               .catch((e: AxiosError) => {
                    const err: IHttpErrorResponseModel = e.response?.data
                    if (err.errors && err.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR) {
                         for (const key in err.errors) helpers.setFieldError(key, err.errors[key])
                    }
                    helpers.setStatus(err?.detail)
                    toast.error(err?.detail)
                    helpers.setSubmitting(false)
               })
     }

     // --- VUE INTRODUCTION (SMART & SEXY) ---
     if (showIntro) {
          return (
               <div className="p-8 text-center animate__animated animate__fadeIn">
                    <div className="mb-10">
                         <div className="symbol symbol-100px symbol-circle mb-5 shadow-sm border border-2 border-white">
                              <span className="symbol-label bg-light-primary">
                                   <i className="bi bi-calendar-check text-primary fs-3x"></i>
                              </span>
                         </div>
                         <h2 className="fw-boldest text-gray-900 fs-1 mb-3">Programmer votre réunion</h2>
                    </div>

                    <div className="row g-5 mb-12">
                         <IntroFeature icon="bi-google" color="danger" title="Synchro Google" desc="L'événement est ajouté directement dans votre calendrier Google Workspace." />
                         <IntroFeature icon="bi-camera-video" color="primary" title="Lien Meet" desc="Génération automatique d'un lien Google Meet sécurisé pour vos visios." />
                         <IntroFeature
                              icon="bi-envelope-check"
                              color="success"
                              title="Emailing"
                              desc="Les participants reçoivent une invitation avec tous les détails."
                         />
                    </div>

                    <div className="d-flex flex-column flex-sm-row justify-content-center gap-4 border-top pt-10">
                         <button className="btn btn-lg btn-light-primary fw-boldest" onClick={() => handleContinue(false)}>
                              CONTINUER
                         </button>
                         <button className="btn btn-lg btn-primary fw-boldest shadow-sm" onClick={() => handleContinue(true)}>
                              CONTINUER ET NE PLUS AFFICHER
                         </button>
                    </div>
               </div>
          )
     }

     // --- VUE FORMULAIRE ---
     return (
          <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema} enableReinitialize={true}>
               {helpers => (
                    <Form noValidate className="p-2 animate__animated animate__fadeIn">
                         {helpers.status && (
                              <MyAlert type="danger" classNames="mb-8">
                                   {helpers.status}
                              </MyAlert>
                         )}

                         <InterviewContext candidat={candidat} candidatures={candidatures} isLoadingCandidatures={isLoadingCandidatures} helpers={helpers} />

                         <InterviewConfiguration
                              helpers={helpers}
                              currentUser={currentUser}
                              onTypeChange={val => helpers.setFieldValue("reunionTitre", getTitreReunion(val as string))}
                         />

                         <InterviewCalendar helpers={helpers} />

                         <InterviewParticipants helpers={helpers} />

                         <div className="d-flex justify-content-end">
                              <button className="btn btn-primary fw-boldest px-12 py-4 shadow-sm" type="submit" disabled={helpers.isSubmitting}>
                                   {!helpers.isSubmitting ? (
                                        <>
                                             <i className="fas fa-calendar-check me-2 fs-4"></i> PROGRAMMER
                                        </>
                                   ) : (
                                        <MySimpleSpinner size="sm" />
                                   )}
                              </button>
                         </div>
                    </Form>
               )}
          </Formik>
     )
}

// Petit composant utilitaire interne pour les features
const IntroFeature = ({ icon, color, title, desc }: { icon: string; color: string; title: string; desc: string }) => (
     <div className="col-md-4">
          <div className="card h-100 border-0 bg-light-secondary bg-opacity-40 p-6 rounded-4 text-center">
               <i className={clsx("bi fs-2x mb-4 d-block", icon, `text-${color}`)}></i>
               <h5 className="fw-boldest text-gray-800 mb-2">{title}</h5>
               <p className="text-gray-600 fs-8 mb-0">{desc}</p>
          </div>
     </div>
)

export default ProgrammerReunion
